import { useMe } from "@@/authentication/me/me-context";
import { VerticalDivider } from "@@/shared/dividers/vertical-divider";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { TextBox } from "@@/shared/text/text-box";
import { ProviderLogoLinkCircle } from "@@/store-fronts/towni/sections/providers/provider-logo-link-circle";
import { useTheme } from "@emotion/react";
import type { Product, Provider } from "@towni/common";
import { hejalfredScope, isVoucher } from "@towni/common";
import { useNavigate } from "react-router";

type Props = {
    voucher: Product | undefined;
    provider: Provider | undefined;
    unavailable?: boolean;
    minWidth?: React.CSSProperties["minWidth"];
};
const VoucherCardWithChin = (props: Props) => {
    const { voucher, provider } = props;
    const [me] = useMe();
    const theme = useTheme();
    const navigate = useNavigate();

    if (voucher && !isVoucher(voucher)) return null;

    const poster = voucher?.meta.images[0];

    if (!voucher) return null;
    const hasHejAlfredScope = me?.scopes.includes(hejalfredScope);
    const isInactive = !!props.unavailable;
    if (!hasHejAlfredScope && isInactive) {
        return null;
    }

    const onClick = () => {
        void navigate(`/vouchers/${voucher._id}`);
    };
    return (
        <FlexColumn
            fillParentWidth
            radius={10}
            minWidth={props.minWidth}
            css={{
                willChange: "filter",
                position: "relative",
                opacity: isInactive ? 0.5 : 1,
                filter: isInactive
                    ? "blur(1px) grayscale(100%) drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.15))"
                    : "drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.15))",
            }}>
            <FlexColumn
                fillParentWidth
                radius={10}
                css={{
                    overflow: "hidden",
                    cursor: "pointer",
                }}
                onClick={onClick}
                background={{
                    imageReference: poster,
                    fit: "COVER",
                    imageSize: { imageWidth: 500 },
                }}>
                <FlexRow padding={{ all: 15, bottom: 130 }}>
                    <ProviderLogoLinkCircle
                        provider={provider}
                        size="3XS"
                        hideTitle
                    />
                </FlexRow>
                <FlexRow
                    background={{ color: theme.colors.white }}
                    padding={{ all: 20 }}
                    mainAxis="space-between"
                    crossAxis="center">
                    <FlexColumn>
                        <TextBox
                            text={voucher.meta.title}
                            weight="900"
                            lineClamp={1}
                        />
                        <VerticalDivider size="3XS" />
                        <TextBox
                            text={voucher.meta.subtitle}
                            weight="400"
                            color={theme.colors.black.light40}
                            lineClamp={1}
                        />
                    </FlexColumn>
                    <FlexColumn css={{ cursor: "pointer" }}>
                        <svg
                            width="64"
                            height="42"
                            viewBox="0 0 64 42"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M63.2254 26.25C60.5326 26.25 58.3498 23.8995 58.3498 21C58.3498 18.1005 60.5326 15.75 63.2254 15.75V5.25C63.2254 2.35047 61.0425 0 58.3498 0H4.87559C2.18284 0 0 2.35047 0 5.25V15.75C2.69275 15.75 4.87559 18.1005 4.87559 21C4.87559 23.8995 2.69275 26.25 0 26.25V36.75C0 39.6495 2.18284 42 4.87559 42H58.3498C61.0425 42 63.2254 39.6495 63.2254 36.75V26.25ZM20.7383 16.088H22.5023V20.288H22.5863L26.6883 16.088H29.0683L24.4343 20.666L29.3763 26H26.8983L22.6003 21.128H22.5023V26H20.7383V16.088ZM29.8323 22.64C29.8323 22.108 29.9256 21.6273 30.1123 21.198C30.3083 20.7593 30.5696 20.386 30.8963 20.078C31.2229 19.77 31.6103 19.532 32.0583 19.364C32.5063 19.196 32.9823 19.112 33.4863 19.112C33.9903 19.112 34.4663 19.196 34.9143 19.364C35.3623 19.532 35.7496 19.77 36.0763 20.078C36.4029 20.386 36.6596 20.7593 36.8463 21.198C37.0423 21.6273 37.1403 22.108 37.1403 22.64C37.1403 23.172 37.0423 23.6573 36.8463 24.096C36.6596 24.5253 36.4029 24.894 36.0763 25.202C35.7496 25.51 35.3623 25.748 34.9143 25.916C34.4663 26.084 33.9903 26.168 33.4863 26.168C32.9823 26.168 32.5063 26.084 32.0583 25.916C31.6103 25.748 31.2229 25.51 30.8963 25.202C30.5696 24.894 30.3083 24.5253 30.1123 24.096C29.9256 23.6573 29.8323 23.172 29.8323 22.64ZM31.5123 22.64C31.5123 22.9013 31.5543 23.1533 31.6383 23.396C31.7316 23.6387 31.8623 23.8533 32.0303 24.04C32.2076 24.2267 32.4176 24.376 32.6603 24.488C32.9029 24.6 33.1783 24.656 33.4863 24.656C33.7943 24.656 34.0696 24.6 34.3123 24.488C34.5549 24.376 34.7603 24.2267 34.9283 24.04C35.1056 23.8533 35.2363 23.6387 35.3203 23.396C35.4136 23.1533 35.4603 22.9013 35.4603 22.64C35.4603 22.3787 35.4136 22.1267 35.3203 21.884C35.2363 21.6413 35.1056 21.4267 34.9283 21.24C34.7603 21.0533 34.5549 20.904 34.3123 20.792C34.0696 20.68 33.7943 20.624 33.4863 20.624C33.1783 20.624 32.9029 20.68 32.6603 20.792C32.4176 20.904 32.2076 21.0533 32.0303 21.24C31.8623 21.4267 31.7316 21.6413 31.6383 21.884C31.5543 22.1267 31.5123 22.3787 31.5123 22.64ZM30.9663 17.096C30.9663 16.8253 31.0643 16.592 31.2603 16.396C31.4656 16.1907 31.7223 16.088 32.0303 16.088C32.3383 16.088 32.5949 16.186 32.8003 16.382C33.0149 16.5687 33.1223 16.8067 33.1223 17.096C33.1223 17.3853 33.0149 17.628 32.8003 17.824C32.5949 18.0107 32.3383 18.104 32.0303 18.104C31.7223 18.104 31.4656 18.006 31.2603 17.81C31.0643 17.6047 30.9663 17.3667 30.9663 17.096ZM33.8503 17.096C33.8503 16.8253 33.9483 16.592 34.1443 16.396C34.3496 16.1907 34.6063 16.088 34.9143 16.088C35.2223 16.088 35.4789 16.186 35.6843 16.382C35.8989 16.5687 36.0063 16.8067 36.0063 17.096C36.0063 17.3853 35.8989 17.628 35.6843 17.824C35.4789 18.0107 35.2223 18.104 34.9143 18.104C34.6063 18.104 34.3496 18.006 34.1443 17.81C33.9483 17.6047 33.8503 17.3667 33.8503 17.096ZM40.3092 19.28H38.7132V29.192H40.3932V25.146H40.4352C40.5192 25.258 40.6265 25.3747 40.7572 25.496C40.8879 25.608 41.0465 25.7153 41.2332 25.818C41.4199 25.9207 41.6345 26.0047 41.8772 26.07C42.1199 26.1353 42.3999 26.168 42.7172 26.168C43.1932 26.168 43.6319 26.0793 44.0332 25.902C44.4345 25.7153 44.7752 25.4633 45.0552 25.146C45.3445 24.8287 45.5685 24.4553 45.7272 24.026C45.8859 23.5967 45.9652 23.1347 45.9652 22.64C45.9652 22.1453 45.8905 21.6833 45.7412 21.254C45.5919 20.8247 45.3725 20.4513 45.0832 20.134C44.7939 19.8167 44.4439 19.5693 44.0332 19.392C43.6225 19.2053 43.1605 19.112 42.6472 19.112C42.1712 19.112 41.7279 19.2053 41.3172 19.392C40.9065 19.5787 40.5799 19.8773 40.3372 20.288H40.3092V19.28ZM44.1452 21.884C44.2385 22.1267 44.2852 22.3787 44.2852 22.64C44.2852 22.9013 44.2385 23.1533 44.1452 23.396C44.0612 23.6387 43.9305 23.8533 43.7532 24.04C43.5852 24.2267 43.3799 24.376 43.1372 24.488C42.8945 24.6 42.6192 24.656 42.3112 24.656C42.0032 24.656 41.7279 24.6 41.4852 24.488C41.2425 24.376 41.0325 24.2267 40.8552 24.04C40.6872 23.8533 40.5565 23.6387 40.4632 23.396C40.3792 23.1533 40.3372 22.9013 40.3372 22.64C40.3372 22.3787 40.3792 22.1267 40.4632 21.884C40.5565 21.6413 40.6872 21.4267 40.8552 21.24C41.0325 21.0533 41.2425 20.904 41.4852 20.792C41.7279 20.68 42.0032 20.624 42.3112 20.624C42.6192 20.624 42.8945 20.68 43.1372 20.792C43.3799 20.904 43.5852 21.0533 43.7532 21.24C43.9305 21.4267 44.0612 21.6413 44.1452 21.884Z"
                                fill="url(#paint0_linear_22_1505)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_22_1505"
                                    x1="4.71832"
                                    y1="-1.09033e-05"
                                    x2="65.8109"
                                    y2="37.8238"
                                    gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#EB0194" />
                                    <stop offset="1" stopColor="#99109A" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </FlexColumn>
                </FlexRow>
            </FlexColumn>
        </FlexColumn>
    );
};

export { VoucherCardWithChin };
